import {useMemo} from 'react';

import type {EagColumn} from '../types/EagColumn';
import type {EagColumnOrGroup} from '../types/EagColumnGroup';
import {buildGroupInfoByKey, buildGroupRowModel, type GroupRowModel} from '../utils/groupRowModel';

type UseGroupRowModelParams = {
  columnConfig: EagColumnOrGroup[];
  /** Visible non-right-pinned columns in render order (layout.nonRightPinnedColumns). */
  nonRightPinnedColumns: EagColumn[];
  /** Visible right-pinned columns in render order (layout.rightPinnedColumns). */
  rightPinnedColumns: EagColumn[];
  columnGroupsVisible: boolean;
};

/**
 * Group-header-row model derived from the LIVE rendered column order, or null
 * when the row is not shown (setting off, or the definition has no groups).
 *
 * Computed once in EagGridTable and shared by GroupHeaderRow (rendering) and
 * useGridDragAndDrop (group-drag routing) so both agree on cell ids and
 * column membership.
 */
export const useGroupRowModel = (params: UseGroupRowModelParams): GroupRowModel | null => {
  // columnConfig is stable after mount, so this memo runs once in practice.
  const groupInfoByKey = useMemo(
    () => buildGroupInfoByKey(params.columnConfig),
    [params.columnConfig]
  );

  const model = useMemo(
    () =>
      buildGroupRowModel({
        nonRightPinnedColumns: params.nonRightPinnedColumns,
        rightPinnedColumns: params.rightPinnedColumns,
        groupInfoByKey,
      }),
    [params.nonRightPinnedColumns, params.rightPinnedColumns, groupInfoByKey]
  );

  // Row visibility mirrors the previous columnConfig-based check: the setting is
  // on AND the definition declares at least one group (even if all its children
  // are currently hidden — the row then renders placeholders only).
  return params.columnGroupsVisible && groupInfoByKey.size > 0 ? model : null;
};
